'use client';
import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { useUserId } from '@/shared/providers/user-provider/use-user';

export const STORAGE_KEY = 'confirmDataPrompts';
export const LOCAL_STORAGE_UPDATED_EVENT = 'confirmLocalStorageUpdated';
const POPUP_DELAY_MS = 24 * 60 * 60 * 1000;

type PromptState = {
  viewCount: number;
  lastPopupTime: number | null;
  showPopup: boolean;
  showBanner: boolean;
  sessionToken: string;
};

type UserStorageEntry = {
  userId: string | number;
  state: PromptState;
};

const createInitialState = (sessionToken: string): PromptState => ({
  viewCount: 1,
  lastPopupTime: null,
  showPopup: false,
  showBanner: false,
  sessionToken,
});

const readStorage = (): UserStorageEntry[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return [];
  }
};

const computeShowBanner = (state: PromptState, now: number): boolean =>
  state.viewCount >= 3 &&
  state.lastPopupTime !== null &&
  now - state.lastPopupTime >= POPUP_DELAY_MS;

// Only a fresh view may open the popup; recomputing showPopup on every
// effect run (e.g. a session-token refresh) would clobber a dismissal
// already written by useConfirmDataPrompt's onPopupClose.
const withDerivedFlags = (state: PromptState, now: number): PromptState => {
  const showPopup = state.viewCount === 2;
  const lastPopupTime = showPopup ? now : state.lastPopupTime;
  return {
    ...state,
    showPopup,
    lastPopupTime,
    showBanner: computeShowBanner({ ...state, lastPopupTime }, now),
  };
};

const withRecomputedBanner = (state: PromptState, now: number): PromptState => ({
  ...state,
  showBanner: computeShowBanner(state, now),
});

// Side-effect only: seeds/updates localStorage that useConfirmDataPrompt (hooks.ts) reads.
export default function ConfirmInitializer() {
  const userId = useUserId() ?? '';
  const currentSessionToken = Cookies.get('sessionId') || '';
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (!userId) return;

    const now = Date.now();
    const storageArray = readStorage();
    const currentUserIndex = storageArray.findIndex(
      (entry) => entry.userId === userId
    );
    const currentUserEntry =
      currentUserIndex !== -1 ? storageArray[currentUserIndex] : null;

    const isFreshView = isInitialMount.current;
    isInitialMount.current = false;

    const baseState = currentUserEntry
      ? { ...currentUserEntry.state, sessionToken: currentSessionToken }
      : createInitialState(currentSessionToken);

    if (currentUserEntry && isFreshView) {
      baseState.viewCount += 1;
    }

    const updatedState = isFreshView
      ? withDerivedFlags(baseState, now)
      : withRecomputedBanner(baseState, now);

    if (
      currentUserEntry &&
      JSON.stringify(currentUserEntry.state) === JSON.stringify(updatedState)
    ) {
      return;
    }

    const updatedStorage = [...storageArray];
    if (currentUserIndex !== -1) {
      updatedStorage[currentUserIndex] = { userId, state: updatedState };
    } else {
      updatedStorage.push({ userId, state: updatedState });
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedStorage));
    window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_UPDATED_EVENT));
  }, [userId, currentSessionToken]);

  return null;
}
